import type { SharedContentCard } from '../types/api'
import { getServiceToken } from '../utils'
import type { DetectedSharedUrl } from '../utils/sharedContentUrls'

const FETCH_TIMEOUT_MS = 3000
const MAX_TITLE_CHARS = 200

/**
 * Shared-preview metadata exactly as the mobile app sends it: a single card
 * under `post` or `property`, merged into the message's `metadata` object.
 */
export type SharedContentMetadata = { post: SharedContentCard } | { property: SharedContentCard }

const trimBase = (base: string | undefined): string => (base ?? '').replace(/\/+$/, '')

// Post text arrives as rich-text HTML (e.g. "<p>Test</p>"). Cards render plain
// text, so strip markup and decode entities before trimming.
const stripHtml = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
  } catch {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  }
}

const trimTitle = (value: string): string | null => value.slice(0, MAX_TITLE_CHARS) || null

const fetchJsonWithTimeout = async (url: string, headers: Record<string, string>): Promise<unknown> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const res = await fetch(url, { headers, signal: controller.signal })
    if (!res.ok) return null
    return (await res.json()) as unknown
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any -- external API payloads are untyped */
const firstString = (values: unknown[]): string | null => {
  const found = values.find((value) => typeof value === 'string' && value.trim().length > 0)
  return typeof found === 'string' ? found : null
}

const buildPostCard = (payload: any, link: string): SharedContentCard | null => {
  const data = payload?.data
  if (!data || typeof data !== 'object') return null

  const firstImage = (Array.isArray(data.images) ? data.images : [])
    .map((entry: any) => entry?.image)
    .find((img: unknown): img is string => typeof img === 'string' && img.length > 0)

  // Feed video entries carry no thumbnail field ({id, video}), but post videos
  // live on Bunny Stream, which serves a sibling thumbnail.jpg next to every
  // playlist.m3u8 — derive it so a video-only post still gets a cover image.
  const firstVideoUrl = data.videos?.[0]?.video
  const derivedVideoThumb =
    typeof firstVideoUrl === 'string' && /\/playlist\.m3u8(\?|$)/i.test(firstVideoUrl)
      ? firstVideoUrl.replace(/playlist\.m3u8(\?.*)?$/i, 'thumbnail.jpg')
      : null

  return {
    hashkey: firstString([data.feedHashkey, data.hashkey]),
    link,
    title: typeof data.text === 'string' ? trimTitle(stripHtml(data.text)) : null,
    image: firstString([firstImage, data.videos?.[0]?.thumbnail, derivedVideoThumb]),
    subtitle: null,
    caption: null,
    poster: {
      hashkey: firstString([data.user?.hashkey, data.userHashkey]),
      name: firstString([data.user?.agentInformation?.name, data.user?.companyInformation?.officeNameEn]),
      avatar: firstString([data.user?.avatar]),
    },
  }
}

/** "AED 65,000" — mobile puts the price in `title` for property cards. */
const formatPrice = (price: unknown, currency: unknown): string | null => {
  if (price === null || price === undefined || price === '') return null
  const amount = Number(price)
  const shown = Number.isFinite(amount) ? amount.toLocaleString('en-US') : String(price)
  const prefix = typeof currency === 'string' && currency.trim() ? `${currency.trim()} ` : ''
  return `${prefix}${shown}`
}

const buildPropertyCard = (payload: any, link: string, key: string): SharedContentCard | null => {
  const data = payload?.data
  if (!data || typeof data !== 'object') return null

  const firstImage = (Array.isArray(data.mainImage) ? data.mainImage : []).find(
    (img: unknown): img is string => typeof img === 'string' && img.length > 0,
  )

  return {
    hashkey: key,
    link,
    title: formatPrice(data.price, data.currency) ?? firstString([data.title]),
    image: firstString([firstImage]),
    // Mobile shows only the price under the poster name; keeping subtitle null
    // makes a web-shared property render identically on both platforms.
    subtitle: null,
    caption: null,
    poster: firstString([data.agentName]) ? { name: firstString([data.agentName]) } : null,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const fetchOne = async (detected: DetectedSharedUrl): Promise<SharedContentMetadata | null> => {
  if (detected.type === 'post') {
    const base = trimBase(import.meta.env.VITE_FEED_API_URL as string | undefined)
    if (!base) return null
    const token = getServiceToken()
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    const payload = await fetchJsonWithTimeout(`${base}/${encodeURIComponent(detected.key)}`, headers)
    const post = buildPostCard(payload, detected.url)
    return post ? { post } : null
  }

  const base = trimBase(import.meta.env.VITE_PROPERTIES_API_URL as string | undefined)
  if (!base) return null
  const payload = await fetchJsonWithTimeout(
    `${base}/api/properties/${encodeURIComponent(detected.key)}/seo-meta`,
    { Accept: 'application/json' },
  )
  const property = buildPropertyCard(payload, detected.url, detected.key)
  return property ? { property } : null
}

/**
 * Resolve detected platform URLs into shared-preview metadata. The payload
 * holds a single card, so the first URL that resolves wins; the rest stay
 * plain links in the message text. Each fetch is bounded by a 3s timeout and
 * failures resolve to null, so a message always sends. Never rejects.
 */
export const fetchSharedContentMetadata = async (
  detected: DetectedSharedUrl[],
): Promise<SharedContentMetadata | null> => {
  for (const entry of detected) {
    const built = await fetchOne(entry).catch(() => null)
    if (built) return built
  }
  return null
}
